// src/services/friendshipService.js
import { auth, db, functions } from '../firebase';
import { 
  getDocs,
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  documentId
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

/**
 * FriendshipService - Subcollection-based friend management service
 * 
 * This service implements the new friends system architecture using subcollections
 * at /users/{userId}/friends/{friendId} instead of arrays in user documents.
 * 
 * Key improvements:
 * - No document size limits (unlimited friends per user)
 * - Rich metadata support (status, timestamps, initiator tracking)
 * - Better query performance with pagination support
 * - Single status field for relationship state management
 */
class FriendshipService {
  constructor() {
    // Initialize Cloud Function references for write operations
    this.sendFriendRequestFunction = httpsCallable(functions, 'sendFriendRequest');
    this.acceptFriendRequestFunction = httpsCallable(functions, 'acceptFriendRequest');
    this.declineFriendRequestFunction = httpsCallable(functions, 'declineFriendRequest');
    this.cancelFriendRequestFunction = httpsCallable(functions, 'cancelFriendRequest');
    this.removeFriendFunction = httpsCallable(functions, 'removeFriend');
    this.blockUserFunction = httpsCallable(functions, 'blockUser');
    this.unblockUserFunction = httpsCallable(functions, 'unblockUser');
    
    // Server-side read operations for security compliance
    this.checkFriendshipStatusFunction = httpsCallable(functions, 'checkFriendshipStatus');
    this.friendSearchCache = new Map();
    this.friendSearchInflight = new Map();
    this.friendSearchCacheVersions = new Map();
  }

  // ============================================
  // Friend List Operations
  // ============================================

  /**
   * Subscribe to real-time updates of accepted friends for the current user
   * @param {Function} callback - Callback function for friend list updates
   * @returns {Function} Unsubscribe function
   */
  subscribeFriends(callback) {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      callback([]);
      return () => {};
    }

    const friendsQuery = query(
      collection(db, 'users', currentUser.uid, 'friends'),
      where('status', '==', 'accepted'),
      orderBy('intimacy.score', 'desc'),
      orderBy('updatedAt', 'desc'),
      orderBy(documentId(), 'desc') // Tiebreaker for stable pagination
    );

    return onSnapshot(
      friendsQuery,
      (snapshot) => {
        const friends = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
          acceptedAt: doc.data().acceptedAt?.toDate?.() || doc.data().acceptedAt,
          updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt
        }));
        callback(friends);
      },
      (error) => {
        console.error('Error listening to friend updates:', error);
        callback([]);
      }
    );
  }

  // ============================================
  // Friend Request Operations
  // ============================================

  /**
   * Get all pending friend requests for the current user
   * @returns {Promise<Array>} List of pending friend request objects
   */
  async getFriendRequests() {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    try {
      const requestsQuery = query(
        collection(db, 'users', currentUser.uid, 'friends'),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(requestsQuery);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
        updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt
      }));
    } catch (error) {
      console.error('Error fetching friend requests:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time updates of pending friend requests
   * @param {Function} callback - Callback function for request updates
   * @returns {Function} Unsubscribe function
   */
  subscribeFriendRequests(callback) {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      callback([]);
      return () => {};
    }

    const requestsQuery = query(
      collection(db, 'users', currentUser.uid, 'friends'),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(
      requestsQuery,
      (snapshot) => {
        const requests = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
          updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt
        }));
        callback(requests);
      },
      (error) => {
        console.error('Error listening to friend request updates:', error);
        callback([]);
      }
    );
  }

  /**
   * Send a friend request to another user
   * @param {string} targetUserId - The user ID to send the request to
   * @returns {Promise<Object>} Result with success status
   */
  async sendFriendRequest(targetUserId) {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    try {
      const result = await this.sendFriendRequestFunction({ targetUserId });
      return result.data;
    } catch (error) {
      console.error('Error sending friend request:', error);
      throw error;
    }
  }

  /**
   * Accept a friend request
   * @param {string} requesterId - The user ID of the requester
   * @returns {Promise<Object>} Result with success status
   */
  async acceptFriendRequest(requesterId) {
    try {
      const result = await this.acceptFriendRequestFunction({ requesterId });
      const currentUser = auth.currentUser;
      if (currentUser?.uid) {
        this.clearFriendSearchCache(currentUser.uid);
      }
      return result.data;
    } catch (error) {
      console.error('Error accepting friend request:', error);
      throw error;
    }
  }

  /**
   * Decline a friend request
   * @param {string} requesterId - The user ID of the requester
   * @returns {Promise<Object>} Result with success status
   */
  async declineFriendRequest(requesterId) {
    try {
      const result = await this.declineFriendRequestFunction({ requesterId });
      return result.data;
    } catch (error) {
      console.error('Error declining friend request:', error);
      throw error;
    }
  }

  /**
   * Cancel a sent friend request
   * @param {string} targetUserId - The user ID of the request recipient
   * @returns {Promise<Object>} Result with success status
   */
  async cancelFriendRequest(targetUserId) {
    try {
      const result = await this.cancelFriendRequestFunction({ targetUserId });
      return result.data;
    } catch (error) {
      console.error('Error cancelling friend request:', error);
      throw error;
    }
  }

  // ============================================
  // Friend Management Operations
  // ============================================

  /**
   * Remove a friend relationship
   * @param {string} friendId - The user ID of the friend to remove
   * @returns {Promise<Object>} Result with success status
   */
  async removeFriend(friendId) {
    try {
      const result = await this.removeFriendFunction({ friendId });
      const currentUser = auth.currentUser;
      if (currentUser?.uid) {
        this.clearFriendSearchCache(currentUser.uid);
      }
      return result.data;
    } catch (error) {
      console.error('Error removing friend:', error);
      throw error;
    }
  }

  /**
   * Block a user
   * @param {string} userId - The user ID to block
   * @returns {Promise<Object>} Result with success status
   */
  async blockUser(userId) {
    try {
      const result = await this.blockUserFunction({ targetUserId: userId });
      const currentUser = auth.currentUser;
      if (currentUser?.uid) {
        this.clearFriendSearchCache(currentUser.uid);
      }
      return result.data;
    } catch (error) {
      console.error('Error blocking user:', error);
      throw error;
    }
  }

  /**
   * Unblock a user
   * @param {string} userId - The user ID to unblock
   * @returns {Promise<Object>} Result with success status
   */
  async unblockUser(userId) {
    try {
      const result = await this.unblockUserFunction({ targetUserId: userId });
      const currentUser = auth.currentUser;
      if (currentUser?.uid) {
        this.clearFriendSearchCache(currentUser.uid);
      }
      return result.data;
    } catch (error) {
      console.error('Error unblocking user:', error);
      throw error;
    }
  }

  /**
   * Get all blocked users for the current user
   * @returns {Promise<Array>} List of blocked user objects
   */
  async getBlockedUsers() {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    try {
      const blockedQuery = query(
        collection(db, 'users', currentUser.uid, 'friends'),
        where('status', '==', 'blocked'),
        orderBy('updatedAt', 'desc')
      );

      const snapshot = await getDocs(blockedQuery);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
        updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt
      }));
    } catch (error) {
      console.error('Error fetching blocked users:', error);
      throw error;
    }
  }

  // ============================================
  // Status and Discovery Operations
  // ============================================

  /**
   * Check the friendship status between the current user and another user
   * Uses server-side Cloud Function to comply with ADR security model
   * @param {string} userId - The user ID to check relationship with
   * @returns {Promise<string>} Relationship status ('self', 'friends', 'requested', 'pending', 'blocked', 'blocked_by', 'none')
   */
  async checkFriendshipStatus(userId) {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');
    if (currentUser.uid === userId) return 'self';

    try {
      // Use Cloud Function to check status (server-side has permission to read cross-user data)
      const result = await this.checkFriendshipStatusFunction({ targetUserId: userId });
      return result.data.status;
    } catch (error) {
      console.error('Error checking friendship status:', error);
      // Return conservative default on error
      return 'none';
    }
  }

  /**
   * Clears cached friend-search candidates.
   * @param {string|null} userId - Optional user ID to clear only that user's cache
   */
  clearFriendSearchCache(userId = null) {
    const keysToClear = new Set();

    const shouldClear = (key) => {
      return !userId || key.startsWith(`${userId}:`);
    };

    for (const key of this.friendSearchCache.keys()) {
      if (shouldClear(key)) {
        keysToClear.add(key);
      }
    }
    for (const key of this.friendSearchInflight.keys()) {
      if (shouldClear(key)) {
        keysToClear.add(key);
      }
    }
    for (const key of this.friendSearchCacheVersions.keys()) {
      if (shouldClear(key)) {
        keysToClear.add(key);
      }
    }

    for (const key of keysToClear) {
      this.friendSearchCache.delete(key);
      this.friendSearchInflight.delete(key);
      this.bumpFriendSearchCacheVersion(key);
    }
  }

  getFriendSearchCacheVersion(cacheKey) {
    return this.friendSearchCacheVersions.get(cacheKey) ?? 0;
  }

  bumpFriendSearchCacheVersion(cacheKey) {
    const nextVersion = this.getFriendSearchCacheVersion(cacheKey) + 1;
    this.friendSearchCacheVersions.set(cacheKey, nextVersion);
    return nextVersion;
  }

  async loadFriendSearchCandidates(userId, limitCount, options = {}) {
    const cacheTtlMs = Math.max(options.cacheTtlMs ?? 30 * 1000, 0);
    const forceRefresh = options.forceRefresh === true;
    const cacheKey = `${userId}:${limitCount}`;

    if (forceRefresh) {
      this.friendSearchCache.delete(cacheKey);
      this.bumpFriendSearchCacheVersion(cacheKey);
      this.friendSearchInflight.delete(cacheKey);
    }

    const now = Date.now();
    const cached = this.friendSearchCache.get(cacheKey);

    if (!forceRefresh && cached && cached.expiresAt > now) {
      return cached.candidates;
    }

    const inflight = this.friendSearchInflight.get(cacheKey);
    if (!forceRefresh && inflight) {
      return inflight;
    }

    const requestVersion = this.getFriendSearchCacheVersion(cacheKey);
    const fetchPromise = (async () => {
      const friendsQuery = query(
        collection(db, 'users', userId, 'friends'),
        where('status', '==', 'accepted'),
        orderBy('updatedAt', 'desc'),
        orderBy(documentId(), 'desc'),
        limit(limitCount)
      );

      const friendsSnap = await getDocs(friendsQuery);
      if (friendsSnap.empty) {
        if (this.getFriendSearchCacheVersion(cacheKey) === requestVersion) {
          this.friendSearchCache.set(cacheKey, {
            expiresAt: Date.now() + cacheTtlMs,
            candidates: []
          });
        }
        return [];
      }

      const friends = friendsSnap.docs.map((entry) => ({
        id: entry.id,
        updatedAt:
          entry.data().updatedAt?.toDate?.() || entry.data().updatedAt || new Date(0)
      }));

      const friendIds = friends.map((friend) => friend.id);
      const chunks = [];
      for (let i = 0; i < friendIds.length; i += 10) {
        chunks.push(friendIds.slice(i, i + 10));
      }

      const userSnapshots = await Promise.all(
        chunks.map((chunk) => {
          const usersQuery = query(
            collection(db, 'users'),
            where(documentId(), 'in', chunk)
          );
          return getDocs(usersQuery);
        })
      );

      const friendUpdatedAtMap = new Map(
        friends.map((friend) => [friend.id, friend.updatedAt])
      );

      const candidates = [];
      userSnapshots.forEach((snapshot) => {
        snapshot.docs.forEach((entry) => {
          candidates.push({
            id: entry.id,
            ...entry.data(),
            __friendUpdatedAt: friendUpdatedAtMap.get(entry.id) || new Date(0)
          });
        });
      });

      if (this.getFriendSearchCacheVersion(cacheKey) === requestVersion) {
        this.friendSearchCache.set(cacheKey, {
          expiresAt: Date.now() + cacheTtlMs,
          candidates
        });
      }

      return candidates;
    })();

    this.friendSearchInflight.set(cacheKey, fetchPromise);
    try {
      return await fetchPromise;
    } finally {
      if (
        this.getFriendSearchCacheVersion(cacheKey) === requestVersion &&
        this.friendSearchInflight.get(cacheKey) === fetchPromise
      ) {
        this.friendSearchInflight.delete(cacheKey);
      }
    }
  }

  /**
   * Search among the current user's accepted friends.
   * - Fast path: fetch up to `limitCount` most recent friends, batch-load their user docs,
   *   then filter client-side by the search term.
   * - Ranking: prefix > substring > recent (updatedAt desc).
   *
   * @param {string} searchTerm
   * @param {{limitCount?: number}} options
   * @returns {Promise<Array>} Array of user public profiles (id, displayName, username, email, profilePictureUrl)
   */
  async searchFriends(searchTerm, options = {}) {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    const term = (searchTerm || '').trim();
    if (!term) return [];

    const limitCount = Math.min(Math.max(options.limitCount || 50, 1), 100); // clamp 1..100
    const candidates = await this.loadFriendSearchCandidates(
      currentUser.uid,
      limitCount,
      options
    );
    if (candidates.length === 0) return [];

    // 3) Filter + rank client-side
    const t = term.toLowerCase();
    const digits = term.replace(/\D+/g, '');

    const scoreRow = (u) => {
      const dn = (u.displayName || '').toLowerCase();
      const un = (u.username || '').toLowerCase();
      const em = (u.email || '').toLowerCase();
      const pn = (u.phoneNumber || '').replace(/\D+/g, '');

      const fields = [dn, un, em];
      const isPrefix = fields.some(v => v && v.startsWith(t));
      const isSubstr = fields.some(v => v && v.includes(t));
      const phoneHit = digits.length >= 3 && pn.includes(digits);

      // Higher score is better. Prefix gets the highest boost, then substring, then phone,
      // then recency by updatedAt (later is better).
      const recentMs = u.__friendUpdatedAt?.getTime?.() || 0;
      let score = 0;
      if (isPrefix) score += 1000;
      if (isSubstr) score += 100;
      if (phoneHit) score += 50;
      score += recentMs / 1e11; // keep small to not dominate
      return { pass: isPrefix || isSubstr || phoneHit, score };
    };

    const filtered = candidates
      .map(u => ({ u, s: scoreRow(u) }))
      .filter(x => x.s.pass)
      .sort((a, b) => b.s.score - a.s.score)
      .map(x => ({
        id: x.u.id,
        displayName: x.u.displayName,
        username: x.u.username,
        email: x.u.email,
        profilePictureUrl: x.u.profilePictureUrl
      }));

    return filtered;
  }

  /**
   * Search for users by name, username, or phone number
   * @param {string} searchTerm - The term to search for
   * @returns {Promise<Array>} List of user objects matching the search
   */
  async searchUsers(searchTerm) {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');
    if (!searchTerm.trim()) return [];

    try {
      // Create queries for different search criteria
      const queries = [
        // Search by username (case-insensitive)
        query(collection(db, "users"), 
          where("username", ">=", searchTerm.toLowerCase()),
          where("username", "<=", searchTerm.toLowerCase() + '\uf8ff')
        ),
        // Search by display name
        query(collection(db, "users"), 
          where("displayName", ">=", searchTerm),
          where("displayName", "<=", searchTerm + '\uf8ff')
        ),
        // Search by phone number
        query(collection(db, "users"), 
          where("phoneNumber", ">=", searchTerm),
          where("phoneNumber", "<=", searchTerm + '\uf8ff')
        )
      ];

      const querySnapshots = await Promise.all(queries.map(q => getDocs(q)));
      const userMap = new Map();
      
      // Combine and deduplicate results
      for (const snapshot of querySnapshots) {
        snapshot.docs.forEach(doc => {
          if (doc.id !== currentUser.uid) { // Exclude current user
            userMap.set(doc.id, { id: doc.id, ...doc.data() });
          }
        });
      }

      // Return search results without relationship status to avoid N+1 queries
      // Relationship status can be fetched lazily by the UI when needed
      const results = Array.from(userMap.values());
      
      // For MVP, return without mutual friends count
      // This can be added later with a batch Cloud Function if needed
      return results;
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  }
}

// Export as singleton instance
const friendshipService = new FriendshipService();
export default friendshipService;
